import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  HomeIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  ClockIcon,
  GiftIcon,
  HandRaisedIcon,
  CheckIcon,
  ArrowRightIcon,
  PhoneIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import MarketingHero from '../components/marketing/MarketingHero';
import MarketingCtaBand from '../components/marketing/MarketingCtaBand';

const ProductsPage = () => {
  const [activeTab, setActiveTab] = useState('personal');

  const products = {
    personal: [
      {
        name: 'Premier Checking',
        description: 'A premium checking account with practical benefits for everyday life.',
        icon: BanknotesIcon,
        features: [
          'No monthly maintenance fees',
          'Free ATM access nationwide',
          'Faster payment access',
          'Mobile check deposit',
        ],
        rate: 'No minimum balance',
        popular: true,
        cta: '/signup',
      },
      {
        name: 'High-Yield Savings',
        description: 'Competitive savings options to help you build toward your goals.',
        icon: BuildingLibraryIcon,
        features: [
          'Competitive rates on qualifying balances',
          'No monthly fees',
          'Automatic savings tools',
          'Goal-based savings',
        ],
        rate: 'Rates vary — ask us for current APY',
        cta: '/signup',
      },
      {
        name: 'Rewards Credit Card',
        description: 'Earn rewards on everyday purchases with clear, member-friendly terms.',
        icon: CreditCardIcon,
        features: [
          'Cash back on qualifying purchases',
          'No annual fee on select products',
          'Mobile wallet compatible',
          'Fraud monitoring',
        ],
        rate: 'Intro APR offers may apply',
        cta: '/contact',
      },
      {
        name: 'Home Mortgages',
        description: 'Local lending support whether you are buying, building, or refinancing.',
        icon: HomeIcon,
        features: [
          'Low down payment options on select loans',
          'Fixed and adjustable-rate options',
          'First-time buyer programs',
          'Digital application with human follow-up',
        ],
        rate: 'Rates based on credit & property',
        cta: '/contact',
      },
      {
        name: 'Investment solutions',
        description: 'Guidance and referrals so you can plan confidently for the future.',
        icon: ChartBarIcon,
        features: ['Retirement planning', 'Managed portfolio referrals', 'Education on tax-advantaged accounts'],
        rate: 'Custom to your goals',
        cta: '/contact',
      },
      {
        name: 'Personal loans',
        description: 'Flexible financing for consolidation, major purchases, and life events.',
        icon: BanknotesIcon,
        features: [
          'Streamlined application',
          'Competitive fixed rates',
          'No prepayment penalties on many products',
          'Predictable monthly payments',
        ],
        rate: 'From competitive APRs',
        cta: '/contact',
      },
    ],
    business: [
      {
        name: 'Business Checking',
        description: 'Operational accounts designed for growing teams and cash flow.',
        icon: BanknotesIcon,
        features: [
          'High transaction allowances on select tiers',
          'Online and mobile banking',
          'Business debit cards',
          'Treasury-friendly reporting',
        ],
        rate: 'Ask about first-year offers',
        popular: true,
        cta: '/signup',
      },
      {
        name: 'Merchant services',
        description: 'Accept payments in-store, online, and on the go.',
        icon: CreditCardIcon,
        features: [
          'Competitive processing options',
          'Fast deposit timelines',
          'Mobile payment solutions',
          'Point-of-sale partnerships',
        ],
        rate: 'Custom pricing',
        cta: '/contact',
      },
      {
        name: 'Business credit card',
        description: 'Control spend, earn rewards, and simplify reconciliation.',
        icon: CreditCardIcon,
        features: [
          'Cash back on qualifying purchases',
          'Employee cards where available',
          'Expense tracking tools',
          'Travel and purchase protections on select cards',
        ],
        rate: 'See current offers',
        cta: '/contact',
      },
      {
        name: 'Business loans',
        description: 'Capital for equipment, expansion, and working cash flow.',
        icon: BanknotesIcon,
        features: [
          'SBA and government-backed options where applicable',
          'Equipment financing',
          'Lines of credit',
          'Owner-occupied commercial real estate',
        ],
        rate: 'From competitive APRs',
        cta: '/contact',
      },
      {
        name: 'Treasury management',
        description: 'Tools to optimize balances, payments, and fraud monitoring.',
        icon: PresentationChartLineIcon,
        features: ['Cash positioning insights', 'Fraud prevention controls', 'Payment automation', 'Reporting tools'],
        rate: 'Tailored packages',
        cta: '/contact',
      },
      {
        name: 'Business savings',
        description: 'Put excess cash to work while keeping liquidity when you need it.',
        icon: BuildingLibraryIcon,
        features: [
          'Business savings and money market options',
          'Certificate ladders',
          'Sweep strategies where available',
        ],
        rate: 'Ask for current business APY',
        cta: '/contact',
      },
    ],
  };

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: 'Secure & protected',
      description: 'NCUA-insured deposits with modern security controls.',
    },
    {
      icon: ClockIcon,
      title: '24/7 access',
      description: 'Digital banking when you want it, with local support when you need it.',
    },
    {
      icon: GiftIcon,
      title: 'Member value',
      description: 'Earnings return to members through better rates and fewer surprises.',
    },
    {
      icon: HandRaisedIcon,
      title: 'Personal service',
      description: 'Real people who know the community—not a distant call script.',
    },
  ];

  const ProductCard = ({ product }) => (
    <div className="group relative">
      {product.popular ? (
        <div className="absolute -right-2 -top-3 z-10 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg sm:text-sm">
          Most popular
        </div>
      ) : null}
      <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition hover:shadow-md">
        <div className="border-b border-stone-100 bg-gradient-to-br from-stone-50 to-white p-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200/80">
            <product.icon className="h-7 w-7 text-emerald-800" />
          </div>
          <h3 className="mt-6 font-serif text-2xl font-bold text-stone-900">{product.name}</h3>
          <p className="mt-3 text-stone-600">{product.description}</p>
          {product.rate ? (
            <div className="mt-6 inline-block rounded-full border border-emerald-200/80 bg-emerald-50 px-4 py-1 text-sm font-medium text-emerald-900">
              {product.rate}
            </div>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col p-8">
          <ul className="mb-8 flex-1 space-y-3">
            {product.features.map((feature, index) => (
              <li key={index} className="flex items-start text-stone-700">
                <CheckIcon className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Link
            to={product.cta}
            className="inline-flex w-full items-center justify-center rounded-2xl bg-emerald-800 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
          >
            {product.cta === '/signup' ? 'Get started' : 'Learn more'}
            <ArrowRightIcon className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <Navigation />

      <MarketingHero
        badge="Accounts · Lending · Business"
        title="Financial solutions for"
        titleAccent="every stage of life"
        description="Discover products and services designed around members—whether you are growing personally or scaling a business."
      >
        <div className="mt-10 inline-flex rounded-full border border-white/15 bg-white/10 p-1.5 shadow-lg backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setActiveTab('personal')}
            className={`rounded-full px-6 py-3 text-sm font-semibold transition sm:px-8 sm:text-base ${
              activeTab === 'personal'
                ? 'bg-white text-emerald-900 shadow-sm'
                : 'text-white/90 hover:bg-white/10'
            }`}
          >
            Personal
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('business')}
            className={`rounded-full px-6 py-3 text-sm font-semibold transition sm:px-8 sm:text-base ${
              activeTab === 'business'
                ? 'bg-white text-emerald-900 shadow-sm'
                : 'text-white/90 hover:bg-white/10'
            }`}
          >
            Business
          </button>
        </div>
      </MarketingHero>

      <section className="border-b border-stone-200/80 bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-6 shadow-sm transition hover:shadow-md"
              >
                <benefit.icon className="mr-4 h-8 w-8 flex-shrink-0 text-emerald-800" />
                <div>
                  <h3 className="font-bold text-stone-900">{benefit.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-stone-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">
            {activeTab === 'personal' ? 'Personal banking solutions' : 'Business banking solutions'}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
            {activeTab === 'personal'
              ? 'Everyday accounts, borrowing, and guidance built for real life—not fine print surprises.'
              : 'Cash management, lending, and payment tools that scale with your team.'}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {products[activeTab].map((product, index) => (
            <ProductCard key={index} product={product} />
          ))}
        </div>
      </section>

      <section className="border-y border-stone-200/80 bg-white py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-serif text-3xl font-bold text-stone-900 md:text-4xl">
            Need help choosing?
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <Link
              to="/contact"
              className="group rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-8 text-center shadow-sm transition hover:shadow-md"
            >
              <PhoneIcon className="mx-auto h-9 w-9 text-emerald-800 transition group-hover:scale-105" />
              <h3 className="mt-4 font-bold text-stone-900">Call us</h3>
              <p className="mt-2 text-sm text-stone-600">Speak with a product specialist.</p>
            </Link>
            <Link
              to="/contact"
              className="group rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-8 text-center shadow-sm transition hover:shadow-md"
            >
              <BuildingLibraryIcon className="mx-auto h-9 w-9 text-emerald-800 transition group-hover:scale-105" />
              <h3 className="mt-4 font-bold text-stone-900">Visit a branch</h3>
              <p className="mt-2 text-sm text-stone-600">Find hours and directions—we will prep for your visit.</p>
            </Link>
            <Link
              to="/contact"
              className="group rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-8 text-center shadow-sm transition hover:shadow-md"
            >
              <CalendarIcon className="mx-auto h-9 w-9 text-emerald-800 transition group-hover:scale-105" />
              <h3 className="mt-4 font-bold text-stone-900">Schedule time</h3>
              <p className="mt-2 text-sm text-stone-600">Request a consultation for lending or business services.</p>
            </Link>
          </div>
        </div>
      </section>

      <MarketingCtaBand
        heading="Ready to get started?"
        body="Join Oakvale Credit Union and put local decisions, member value, and modern tools on your side."
        primaryLabel="Open an account"
        secondaryLabel="Talk with us"
      />

      <Footer />
    </div>
  );
};

export default ProductsPage;
